import React, { lazy } from 'react';
import { RouteProps, Navigate } from 'react-router-dom';
import {
	componentPagesMenu,
	dashboardPagesMenu,
	demoPagesMenu,
	gettingStartedPagesMenu,
	pageLayoutTypesPagesMenu,
	userPageMenu,
} from '../menu';
import Login from '../pages/presentation/auth/Login';
import LogoutPage from '../pages/presentation/auth/LogoutPage';
import MyProfilePage from '../pages/presentation/auth/MyProfilePage';
import UserPage from '../pages/presentation/application-pages/UserPage';
import ViewArticle from '../pages/presentation/application-pages/ViewArticle';
import LandingPage from '../pages/presentation/landing/LandingPage';
import ArticleVerificationPage from '../pages/presentation/application-pages/ArticleVerificationPage';
import PrivacyPolicyPage from '../pages/presentation/legal/PrivacyPolicyPage';
import ReporterPrivacyPolicyPage from '../pages/presentation/legal/ReporterPrivacyPolicyPage';
import DeleteAccountPage from '../pages/presentation/legal/DeleteAccountPage';
import ReporterDeleteAccountPage from '../pages/presentation/legal/ReporterDeleteAccountPage';
import ContactPage from '../pages/presentation/legal/ContactPage';

const LANDING = {
	ANALYTICS_DASHBOARD: lazy(() => import('../pages/presentation/dashboard/AnalyticsDashboard')),
	MANAGE_USERS : lazy(() => import('../pages/presentation/application-pages/ManageUsers')),
	MANAGE_ARTICLES: lazy(() => import('../pages/presentation/application-pages/ManageArticles')),
	CREATE_ARTICLE: lazy(() => import('../pages/presentation/application-pages/CreateArticle')),
	CAMPAIGN_MANAGEMENT: lazy(() => import('../components/campaign/CampaignManagement')),
	PUSH_NOTIFICATIONS: lazy(() => import('../components/pushNotifications/PushNotificationManagement')),
	POST_DATA: lazy(() => import('../pages/presentation/application-pages/PostData')),
	APP_VERSION_MANAGEMENT: lazy(() => import('../pages/presentation/application-pages/AppVersionManagement')),
};

// Web Series Components
// Dynamic section management for web series home screen
const WEB_SERIES = {
	WEB_SERIES_MANAGEMENT: lazy(() => import('../pages/WebSeriesManagement/WebSeriesManagement')),
	SERIES_FORM: lazy(() => import('../pages/WebSeriesManagement/SeriesForm')),
	EPISODE_MANAGEMENT: lazy(() => import('../pages/EpisodeManagement/EpisodeManagement')),
	WEB_SERIES_SECTIONS: lazy(() => import('../pages/WebSeriesSections/WebSeriesSectionManagement')),
	SECTION_SERIES_MANAGEMENT: lazy(() => import('../pages/WebSeriesSections/SectionSeriesManagement')),
	SERIES_STUDIO: lazy(() => import('../pages/SeriesStudio/SeriesStudio')),
	WEB_SERIES_ARTICLES: lazy(() => import('../pages/WebSeriesArticles/WebSeriesArticlesPage')),
};

// Category Management
const CATEGORY_MANAGEMENT = {
	CATEGORY_MANAGEMENT: lazy(() => import('../pages/CategoryManagement/CategoryManagement')),
};

// Home Section Management Components
const HOME_SECTIONS = {
	HOME_SECTION_MANAGEMENT: lazy(() => import('../pages/presentation/home-section/HomeSectionManagement')),
	SECTION_ARTICLE_MANAGEMENT: lazy(() => import('../pages/presentation/home-section/SectionArticleManagement')),
};

// Article Workflow Management Components
const ARTICLE_WORKFLOW = {
	AUTHOR_WORKFLOW: lazy(() => import('../pages/ArticleWorkflow/AuthorWorkflow')),
	EDITOR_WORKFLOW: lazy(() => import('../pages/ArticleWorkflow/EditorWorkflow')),
	PUBLISHER_WORKFLOW: lazy(() => import('../pages/ArticleWorkflow/PublisherWorkflow')),
	ADMIN_WORKFLOW_DASHBOARD: lazy(() => import('../pages/ArticleWorkflow/AdminWorkflowDashboard')),
	STATE_EDITOR_WORKFLOW: lazy(() => import('../pages/ArticleWorkflow/StateEditorWorkflow')),
};

// State Configuration Components
const STATE_CONFIG = {
	STATE_WORKFLOW_CONFIG: lazy(() => import('../pages/StateConfiguration/StateWorkflowConfig')),
	USER_STATE_ROLES: lazy(() => import('../pages/StateConfiguration/UserStateRoles')),
	STATE_LANGUAGE_CONFIG: lazy(() => import('../pages/StateConfiguration/StateLanguageConfig')),
};

// Enterprise Content Distribution — Phase 3
const CONTENT_DISTRIBUTION = {
	COVERAGE_DASHBOARD:      lazy(() => import('../pages/ContentDistribution/CoverageDashboard')),
	ARTICLE_LANGUAGE_EDITOR: lazy(() => import('../pages/ContentDistribution/ArticleLanguageEditor')),
};

// Access Control Management Components
const ACCESS_CONTROL = {
	PERMISSION_MANAGEMENT: lazy(() => import('../pages/AccessControl/PermissionManagement')),
	ROLE_PERMISSION_MATRIX: lazy(() => import('../pages/AccessControl/RolePermissionMatrix')),
	USER_ACCESS_CONTROL: lazy(() => import('../pages/AccessControl/UserAccessControl')),
	AUDIT_LOG: lazy(() => import('../pages/AccessControl/AuditLogPage')),
};

// Reader Platform Components
const READER_PLATFORM = {
	READER_MANAGEMENT: lazy(() => import('../pages/readers/ReaderManagement')),
	READER_DETAIL:     lazy(() => import('../pages/readers/ReaderDetail')),
};

// Reporter Platform Components
const REPORTER_PLATFORM = {
	REPORTER_MANAGEMENT: lazy(() => import('../pages/reporter/ReporterManagement')),
	REPORTER_DETAIL:     lazy(() => import('../pages/reporter/ReporterDetail')),
	ARTICLE_REPORT:      lazy(() => import('../pages/reporter/ArticleReport')),
	REDEMPTION_MANAGEMENT: lazy(() => import('../pages/redemption/RedemptionManagement')),
	COIN_RULES: lazy(() => import('../pages/coinrules/CoinRulesManagement')),
};

// Audio Settings Components (Voice & BGM Configuration)
const AUDIO_SETTINGS = {
	VOICE_CONFIGURATION: lazy(() => import('../pages/AudioSettings/VoiceConfigurationPage')),
	BACKGROUND_MUSIC: lazy(() => import('../pages/AudioSettings/BackgroundMusicPage')),
};

// AI Content Tools
const AI_CONTENT = {
	JOURNALIST_GENERATOR: lazy(() => import('../pages/AIContent/JournalistGeneratorPage')),
};

// News Intelligence Digest
const NEWS_INTELLIGENCE = {
	NEWS_DIGEST:      lazy(() => import('../pages/NewsIntelligence/NewsDigestPage')),
	FEED_SOURCES:     lazy(() => import('../pages/NewsIntelligence/FeedSourcesPage')),
	SYNC_LOGS:        lazy(() => import('../pages/NewsIntelligence/SyncLogsPage')),
	YOUTUBE_DIGEST:   lazy(() => import('../pages/NewsIntelligence/YoutubeDigestPage')),
	YOUTUBE_CHANNELS: lazy(() => import('../pages/NewsIntelligence/YoutubeChannelConfigPage')),
};

// Moderation (Comments + Reports) Components
const MODERATION = {
	COMMENTS_MODERATION: lazy(() => import('../pages/moderation/CommentsModerationPage')),
	REPORTS_MODERATION: lazy(() => import('../pages/moderation/ReportsModerationPage')),
};

// State Coverage Analytics
const STATE_COVERAGE = {
	DASHBOARD: lazy(() => import('../pages/StateCoverage/StateCoverageDashboard')),
};

const AUTH = {
	PAGE_404: lazy(() => import('../pages/presentation/auth/Page404')),
};

const PAGE_LAYOUTS = {
	HEADER_SUBHEADER: lazy(() => import('../pages/presentation/page-layouts/HeaderAndSubheader')),
	HEADER: lazy(() => import('../pages/presentation/page-layouts/OnlyHeader')),
	SUBHEADER: lazy(() => import('../pages/presentation/page-layouts/OnlySubheader')),
	CONTENT: lazy(() => import('../pages/presentation/page-layouts/OnlyContent')),
	BLANK: lazy(() => import('../pages/presentation/page-layouts/Blank')),
	ASIDE: lazy(() => import('../pages/presentation/aside-types/DefaultAsidePage')),
	MINIMIZE_ASIDE: lazy(() => import('../pages/presentation/aside-types/MinimizeAsidePage')),
};

const CONTENT = {
	CONTENTS: lazy(() => import('../pages/documentation/content/ContentListPage')),
	TYPOGRAPHY: lazy(() => import('../pages/documentation/content/TypographyPage')),
	IMAGES: lazy(() => import('../pages/documentation/content/ImagesPage')),
	TABLES: lazy(() => import('../pages/documentation/content/TablesPage')),
	FIGURES: lazy(() => import('../pages/documentation/content/FiguresPage')),
};
const FORMS_PAGE = {
	FORMS: lazy(() => import('../pages/documentation/forms/FormsListPage')),
	FORM_GROUP: lazy(() => import('../pages/documentation/forms/FormGroupPage')),
	FORM_CONTROLS: lazy(() => import('../pages/documentation/forms/FormControlsPage')),
	SELECT: lazy(() => import('../pages/documentation/forms/SelectPage')),
	CHECKS_AND_RADIO: lazy(() => import('../pages/documentation/forms/ChecksAndRadioPage')),
	RANGE: lazy(() => import('../pages/documentation/forms/RangePage')),
	INPUT_GROUP: lazy(() => import('../pages/documentation/forms/InputGroupPage')),
	VALIDATION: lazy(() => import('../pages/documentation/forms/ValidationPage')),
	WIZARD: lazy(() => import('../pages/documentation/forms/WizardPage')),
};
const GETTING_STARTED = {
	INSTALLATION: lazy(() => import('../pages/documentation/getting-started/InstallationPage')),
	DEVELOPMENT: lazy(() => import('../pages/documentation/getting-started/DevelopmentPage')),
	FOLDER: lazy(() => import('../pages/documentation/getting-started/FolderStructurePage')),
	BOOTSTRAP: lazy(() => import('../pages/documentation/getting-started/BootstrapVariablesPage')),
	PROJECT: lazy(() => import('../pages/documentation/getting-started/ProjectStructurePage')),
};
const ROUTES = {
	ROUTER: lazy(() => import('../pages/documentation/routes/RouterPage')),
};
const COMPONENTS_PAGE = {
	COMPONENTS: lazy(() => import('../pages/documentation/components/ComponentsListPage')),
	ACCORDION: lazy(() => import('../pages/documentation/components/AccordionPage')),
	ALERT: lazy(() => import('../pages/documentation/components/AlertPage')),
	BADGE: lazy(() => import('../pages/documentation/components/BadgePage')),
	BREADCRUMB: lazy(() => import('../pages/documentation/components/BreadcrumbPage')),
	BUTTON: lazy(() => import('../pages/documentation/components/ButtonPage')),
	BUTTON_GROUP: lazy(() => import('../pages/documentation/components/ButtonGroupPage')),
	CARD: lazy(() => import('../pages/documentation/components/CardPage')),
	CAROUSEL: lazy(() => import('../pages/documentation/components/CarouselPage')),
	COLLAPSE: lazy(() => import('../pages/documentation/components/CollapsePage')),
	DROPDOWN: lazy(() => import('../pages/documentation/components/DropdownsPage')),
	LIST_GROUP: lazy(() => import('../pages/documentation/components/ListGroupPage')),
	MODAL: lazy(() => import('../pages/documentation/components/ModalPage')),
	NAVS_TABS: lazy(() => import('../pages/documentation/components/NavsTabsPage')),
	OFF_CANVAS: lazy(() => import('../pages/documentation/components/OffCanvasPage')),
	PAGINATION: lazy(() => import('../pages/documentation/components/PaginationPage')),
	POPOVERS: lazy(() => import('../pages/documentation/components/PopoversPage')),
	PROGRESS: lazy(() => import('../pages/documentation/components/ProgressPage')),
	SCROLLSPY: lazy(() => import('../pages/documentation/components/ScrollspyPage')),
	SPINNER: lazy(() => import('../pages/documentation/components/SpinnersPage')),
	TABLE: lazy(() => import('../pages/documentation/components/TablePage')),
	TOASTS: lazy(() => import('../pages/documentation/components/ToastsPage')),
	TOOLTIP: lazy(() => import('../pages/documentation/components/TooltipPage')),
};
const UTILITIES = {
	UTILITIES: lazy(() => import('../pages/documentation/utilities/UtilitiesListPage')),
	API: lazy(() => import('../pages/documentation/utilities/ApiPage')),
	BACKGROUND: lazy(() => import('../pages/documentation/utilities/BackgroundPage')),
	BORDERS: lazy(() => import('../pages/documentation/utilities/BordersPage')),
	COLORS: lazy(() => import('../pages/documentation/utilities/ColorsPage')),
	DISPLAY: lazy(() => import('../pages/documentation/utilities/DisplayPage')),
	FLEX: lazy(() => import('../pages/documentation/utilities/FlexPage')),
	FLOAT: lazy(() => import('../pages/documentation/utilities/FloatPage')),
	INTERACTIONS: lazy(() => import('../pages/documentation/utilities/InteractionsPage')),
	OVERFLOW: lazy(() => import('../pages/documentation/utilities/OverflowPage')),
	POSITION: lazy(() => import('../pages/documentation/utilities/PositionPage')),
	SHADOWS: lazy(() => import('../pages/documentation/utilities/ShadowsPage')),
	SIZING: lazy(() => import('../pages/documentation/utilities/SizingPage')),
	SPACING: lazy(() => import('../pages/documentation/utilities/SpacingPage')),
	TEXT: lazy(() => import('../pages/documentation/utilities/TextPage')),
	VERTICAL_ALIGN: lazy(() => import('../pages/documentation/utilities/VerticalAlignPage')),
	VISIBILITY: lazy(() => import('../pages/documentation/utilities/VisibilityPage')),
};
const ICONS = {
	ICONS_LIST: lazy(() => import('../pages/documentation/icons/IconsListPage')),
	ICON: lazy(() => import('../pages/documentation/icons/IconPage')),
	MATERIAL: lazy(() => import('../pages/documentation/icons/MaterialPage')),
};
const CHARTS_PAGE = {
	CHART_LIST: lazy(() => import('../pages/documentation/charts/ChartsListPage')),
	GENERAL_USAGE: lazy(() => import('../pages/documentation/charts/ChartGeneralUsagePage')),
	SPARKLINE: lazy(() => import('../pages/documentation/charts/ChartSparklinePage')),
	LINE: lazy(() => import('../pages/documentation/charts/ChartLinePage')),
	AREA: lazy(() => import('../pages/documentation/charts/ChartAreaPage')),
	COLUMN: lazy(() => import('../pages/documentation/charts/ChartColumnPage')),
	BAR: lazy(() => import('../pages/documentation/charts/ChartBarPage')),
	MIXED: lazy(() => import('../pages/documentation/charts/ChartMixedPage')),
	TIMELINE: lazy(() => import('../pages/documentation/charts/ChartTimelinePage')),
	CANDLESTICK: lazy(() => import('../pages/documentation/charts/ChartCandlestickPage')),
	BOX_WHISKER: lazy(() => import('../pages/documentation/charts/ChartBoxWhiskerPage')),
	PIE_DONUT: lazy(() => import('../pages/documentation/charts/ChartPieDonutPage')),
	RADAR: lazy(() => import('../pages/documentation/charts/ChartRadarPage')),
	POLAR: lazy(() => import('../pages/documentation/charts/ChartPolarPage')),
	RADIAL_BAR: lazy(() => import('../pages/documentation/charts/ChartRadialBarPage')),
	BUBBLE: lazy(() => import('../pages/documentation/charts/ChartBubblePage')),
	SCATTER: lazy(() => import('../pages/documentation/charts/ChartScatterPage')),
	HEAT_MAP: lazy(() => import('../pages/documentation/charts/ChartHeatMapPage')),
	TREE_MAP: lazy(() => import('../pages/documentation/charts/ChartTreeMapPage')),
};
const EXTRA = {
	NOTIFICATION: lazy(() => import('../pages/documentation/extras/NotificationPage')),
	HOOKS: lazy(() => import('../pages/documentation/extras/HooksPage')),
};

const presentation: RouteProps[] = [
	/**
	 * Public landing page - shown at root URL before login
	 * Authenticated users are redirected to /analytics-dashboard by LandingPage component
	 */
	{
		path: '/',
		element: <LandingPage />,
	},
	{
		path: dashboardPagesMenu.analytics.path,
		element: <LANDING.ANALYTICS_DASHBOARD />,
	},
	{
		path: dashboardPagesMenu.manageUsers.path,
		element: <LANDING.MANAGE_USERS />,
	},
	{
		// Secure route using user ID instead of email
		path: `${userPageMenu.userData.path}/:userId`,
		element: <UserPage />,
	},
	{
		path: dashboardPagesMenu.manageArticles.path,
		element: <LANDING.MANAGE_ARTICLES />,
	},
	{
		path: dashboardPagesMenu.createNewArticle.path,
		element: <LANDING.CREATE_ARTICLE />,
	},
	{
		path: dashboardPagesMenu.campaignManagement.path,
		element: <LANDING.CAMPAIGN_MANAGEMENT />,
	},
	{
		path: dashboardPagesMenu.pushNotifications.path,
		element: <LANDING.PUSH_NOTIFICATIONS />,
	},
	{
		path: dashboardPagesMenu.postData.path,
		element: <LANDING.POST_DATA />,
	},
	{
		path: dashboardPagesMenu.appVersionManagement.path,
		element: <LANDING.APP_VERSION_MANAGEMENT />,
	},
	{
		path: dashboardPagesMenu.webSeries.subMenu.allSeries.path,
		element: <WEB_SERIES.WEB_SERIES_MANAGEMENT />,
	},
	{
		path: dashboardPagesMenu.webSeries.subMenu.createSeries.path,
		element: <WEB_SERIES.SERIES_FORM />,
	},
	{
		path: dashboardPagesMenu.webSeries.subMenu.webSeriesSections.path,
		element: <WEB_SERIES.WEB_SERIES_SECTIONS />,
	},
	{
		path: dashboardPagesMenu.homeSectionManagement.path,
		element: <HOME_SECTIONS.HOME_SECTION_MANAGEMENT />,
	},
	{
		path: 'view-article/:id',
		element: <ViewArticle />,
	},
	/**
	 * Public Article Verification Page (unauthenticated)
	 * Allows anyone to verify article authenticity via the watermark code.
	 * Route: /verify/:code  (e.g. /verify/JGS-550E8400)
	 */
	{
		path: 'verify/:code',
		element: <ArticleVerificationPage />,
	},
	/**
	 * Public Privacy Policy page — required for Play Store / App Store compliance.
	 * Route: /privacy-policy  (served at https://justknow.co.in/privacy-policy)
	 */
	{
		path: 'privacy-policy',
		element: <PrivacyPolicyPage />,
	},
	/**
	 * Public Reporter Privacy Policy page — Play Store compliance for Just Know Reporter app.
	 * Route: /reporter-privacy-policy  (served at https://justknow.co.in/reporter-privacy-policy)
	 */
	{
		path: 'reporter-privacy-policy',
		element: <ReporterPrivacyPolicyPage />,
	},
	/**
	 * Public Delete Account page — required for Google Play Store compliance (DPDPA 2023).
	 * Route: /delete-account  (served at https://justknow.co.in/delete-account)
	 */
	{
		path: 'delete-account',
		element: <DeleteAccountPage />,
	},
	/**
	 * Reporter app account deletion page — Play Store URL for JustKnow Reporter app.
	 * Route: /reporter-delete-account  (served at https://justknow.co.in/reporter-delete-account)
	 */
	{
		path: 'reporter-delete-account',
		element: <ReporterDeleteAccountPage />,
	},
	/**
	 * Public Contact Us page — required for Google Play Store policy compliance.
	 * Route: /contact-us  (served at https://justknow.co.in/contact-us)
	 */
	{
		path: 'contact-us',
		element: <ContactPage />,
	},

	/**
	 * Article Workflow Management (Author, Editor, Publisher, Admin, State Editor, Variant Review)
	 */
	{
		path: '/workflow/author',
		element: <ARTICLE_WORKFLOW.AUTHOR_WORKFLOW />,
	},
	{
		path: '/workflow/editor',
		element: <ARTICLE_WORKFLOW.EDITOR_WORKFLOW />,
	},
	{
		path: '/workflow/publisher',
		element: <ARTICLE_WORKFLOW.PUBLISHER_WORKFLOW />,
	},
	{
		path: '/workflow/admin/dashboard',
		element: <ARTICLE_WORKFLOW.ADMIN_WORKFLOW_DASHBOARD />,
	},
	{
		path: '/workflow/state-editor',
		element: <ARTICLE_WORKFLOW.STATE_EDITOR_WORKFLOW />,
	},
	{
		path: '/workflow/variant-review',
		element: null, // Decommissioned — use /admin/content-distribution
	},

	/**
	 * State Configuration (Per-state workflow rules and user-state role assignments)
	 */
	{
		path: '/admin/state-configuration/workflow-config',
		element: <STATE_CONFIG.STATE_WORKFLOW_CONFIG />,
	},
	{
		path: '/admin/state-configuration/user-state-roles',
		element: <STATE_CONFIG.USER_STATE_ROLES />,
	},
	{
		path: '/admin/state-configuration/language-config',
		element: <STATE_CONFIG.STATE_LANGUAGE_CONFIG />,
	},

	// /admin/articles/:articleId/variants — decommissioned; redirected to new editor below

	/**
	 * Enterprise Content Distribution — Phase 3
	 * Coverage Dashboard: matrix view of article × language content status
	 * Article Language Editor: per-article create/edit/workflow for each language
	 */
	{
		path: '/admin/content-distribution',
		element: <CONTENT_DISTRIBUTION.COVERAGE_DASHBOARD />,
	},
	{
		path: '/admin/articles/:articleId/content',
		element: <CONTENT_DISTRIBUTION.ARTICLE_LANGUAGE_EDITOR />,
	},

	/**
	 * Category Management
	 */
	{
		path: '/admin/categories',
		element: <CATEGORY_MANAGEMENT.CATEGORY_MANAGEMENT />,
	},

	/**
	 * Home Section Management
	 */
	{
		path: '/admin/home-sections',
		element: <HOME_SECTIONS.HOME_SECTION_MANAGEMENT />,
	},
	{
		path: '/admin/home-sections/:sectionId/articles',
		element: <HOME_SECTIONS.SECTION_ARTICLE_MANAGEMENT />,
	},

	/**
	 * Web Series Management
	 */
	{
		path: '/admin/web-series',
		element: <WEB_SERIES.WEB_SERIES_MANAGEMENT />,
	},
	{
		path: '/admin/web-series/create',
		element: <WEB_SERIES.SERIES_FORM />,
	},
	{
		path: '/admin/web-series/edit/:seriesId',
		element: <WEB_SERIES.SERIES_FORM />,
	},
	{
		// Must be before /:seriesId to avoid the wildcard catching "articles"
		path: '/admin/web-series/articles',
		element: <WEB_SERIES.WEB_SERIES_ARTICLES />,
	},
	{
		path: '/admin/web-series/:seriesId/episodes',
		element: <WEB_SERIES.EPISODE_MANAGEMENT />,
	},
	{
		// Series Studio — replaces the plain series detail page
		path: '/admin/web-series/:seriesId/studio',
		element: <WEB_SERIES.SERIES_STUDIO />,
	},
	{
		path: '/admin/web-series/:seriesId',
		element: <WEB_SERIES.WEB_SERIES_MANAGEMENT />, // Will show series details in the future
	},

	/**
	 * Web Series Section Management (Dynamic Home Screen Sections)
	 */
	{
		path: '/admin/web-series-sections',
		element: <WEB_SERIES.WEB_SERIES_SECTIONS />,
	},
	{
		path: '/admin/web-series-sections/:sectionId/series',
		element: <WEB_SERIES.SECTION_SERIES_MANAGEMENT />,
	},

	/**
	 * Article Workflow Management
	 */
	{
		path: '/workflow/author',
		element: <ARTICLE_WORKFLOW.AUTHOR_WORKFLOW />,
	},
	{
		path: '/workflow/editor',
		element: <ARTICLE_WORKFLOW.EDITOR_WORKFLOW />,
	},
	{
		path: '/workflow/publisher',
		element: <ARTICLE_WORKFLOW.PUBLISHER_WORKFLOW />,
	},
	{
		path: '/workflow/admin/dashboard',
		element: <ARTICLE_WORKFLOW.ADMIN_WORKFLOW_DASHBOARD />,
	},

	/**
	 * Access Control Management (Admin Only)
	 */
	{
		path: 'admin/access-control/permissions',
		element: <ACCESS_CONTROL.PERMISSION_MANAGEMENT />,
	},
	{
		path: 'admin/access-control/roles',
		element: <ACCESS_CONTROL.ROLE_PERMISSION_MATRIX />,
	},
	{
		path: 'admin/access-control/users',
		element: <ACCESS_CONTROL.USER_ACCESS_CONTROL />,
	},
	{
		path: 'admin/access-control/audit',
		element: <ACCESS_CONTROL.AUDIT_LOG />,
	},

	/**
	 * Audio Settings (Voice & BGM Configuration)
	 */
	/**
	 * Reader Platform
	 */
	{
		path: 'admin/readers',
		element: <READER_PLATFORM.READER_MANAGEMENT />,
	},
	{
		path: 'admin/readers/:userId',
		element: <READER_PLATFORM.READER_DETAIL />,
	},

	/**
	 * Reporter Platform
	 */
	{
		path: 'admin/reporters',
		element: <REPORTER_PLATFORM.REPORTER_MANAGEMENT />,
	},
	{
		path: 'admin/reporters/:reporterId',
		element: <REPORTER_PLATFORM.REPORTER_DETAIL />,
	},
	{
		/**
		 * Reporter Article Report – multi-dimensional article report
		 * filtered by reporter / state / district / date / search.
		 */
		path: 'admin/article-report',
		element: <REPORTER_PLATFORM.ARTICLE_REPORT />,
	},
	{
		path: 'admin/redemptions',
		element: <REPORTER_PLATFORM.REDEMPTION_MANAGEMENT />,
	},
	{
		path: 'admin/coin-rules',
		element: <REPORTER_PLATFORM.COIN_RULES />,
	},

	{
		path: 'admin/audio-settings/voices',
		element: <AUDIO_SETTINGS.VOICE_CONFIGURATION />,
	},
	{
		path: 'admin/audio-settings/bgm',
		element: <AUDIO_SETTINGS.BACKGROUND_MUSIC />,
	},

	/**
	 * AI Content Tools
	 */
	{
		path: 'admin/ai/journalist',
		element: <AI_CONTENT.JOURNALIST_GENERATOR />,
	},

	/**
	 * News Intelligence Digest (admin-only RSS feed aggregation)
	 */
	{
		path: 'admin/news-intelligence/digest',
		element: <NEWS_INTELLIGENCE.NEWS_DIGEST />,
	},
	{
		path: 'admin/news-intelligence/sources',
		element: <NEWS_INTELLIGENCE.FEED_SOURCES />,
	},
	{
		path: 'admin/news-intelligence/sync-logs',
		element: <NEWS_INTELLIGENCE.SYNC_LOGS />,
	},
	{
		path: 'admin/news-intelligence/youtube/digest',
		element: <NEWS_INTELLIGENCE.YOUTUBE_DIGEST />,
	},
	{
		path: 'admin/news-intelligence/youtube/channels',
		element: <NEWS_INTELLIGENCE.YOUTUBE_CHANNELS />,
	},

	/**
	 * Moderation (Comments + Reports)
	 */
	{
		path: 'moderation/comments',
		element: <MODERATION.COMMENTS_MODERATION />,
	},
	{
		path: 'moderation/reports',
		element: <MODERATION.REPORTS_MODERATION />,
	},

	/**
	 * State Coverage Analytics Dashboard
	 */
	{
		path: 'admin/state-coverage',
		element: <STATE_COVERAGE.DASHBOARD />,
	},

	/**
	 * Auth Pages
	 */
	{
		path: 'auth-pages/404',
		element: <AUTH.PAGE_404 />,
	},
	{
		path: 'auth-pages/login',
		element: <Login />,
	},
	{
		// Signup is disabled - users are added via Admin User Management
		path: 'auth-pages/sign-up',
		element: <Navigate to='auth-pages/login' replace />,
	},
	{
		path: demoPagesMenu.logout.path,
		element: <LogoutPage />,
	},
	{
		path: 'my-profile',
		element: <MyProfilePage />,
	},

	/**
	 * Page Layout Types (Keep for future development)
	 */
	{
		path: pageLayoutTypesPagesMenu.blank.path,
		element: <PAGE_LAYOUTS.BLANK />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.headerAndSubheader.path,
		element: <PAGE_LAYOUTS.HEADER_SUBHEADER />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlyHeader.path,
		element: <PAGE_LAYOUTS.HEADER />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlySubheader.path,
		element: <PAGE_LAYOUTS.SUBHEADER />,
	},
	{
		path: pageLayoutTypesPagesMenu.pageLayout.subMenu.onlyContent.path,
		element: <PAGE_LAYOUTS.CONTENT />,
	},
	{
		path: pageLayoutTypesPagesMenu.asideTypes.subMenu.defaultAside.path,
		element: <PAGE_LAYOUTS.ASIDE />,
	},
	{
		path: pageLayoutTypesPagesMenu.asideTypes.subMenu.minimizeAside.path,
		element: <PAGE_LAYOUTS.MINIMIZE_ASIDE />,
	},
	{
		path: 'view-article/:id',
		element: <ViewArticle />,
	},
];
const documentation: RouteProps[] = [
	/**
	 * Getting Started
	 */
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.installation.path,
		element: <GETTING_STARTED.INSTALLATION />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.dev.path,
		element: <GETTING_STARTED.DEVELOPMENT />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.folderStructure.path,
		element: <GETTING_STARTED.FOLDER />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.bootstrapVariables.path,
		element: <GETTING_STARTED.BOOTSTRAP />,
	},
	{
		path: gettingStartedPagesMenu.gettingStarted.subMenu.projectStructure.path,
		element: <GETTING_STARTED.PROJECT />,
	},
	/**
	 * Routes
	 */
	{
		path: gettingStartedPagesMenu.routes.subMenu.router.path,
		element: <ROUTES.ROUTER />,
	},
	/**
	 * Bootstrap
	 */

	/**
	 * Content
	 */
	{
		path: componentPagesMenu.content.path,
		element: <CONTENT.CONTENTS />,
	},
	{
		path: componentPagesMenu.content.subMenu.typography.path,
		element: <CONTENT.TYPOGRAPHY />,
	},
	{
		path: componentPagesMenu.content.subMenu.images.path,
		element: <CONTENT.IMAGES />,
	},
	{
		path: componentPagesMenu.content.subMenu.tables.path,
		element: <CONTENT.TABLES />,
	},
	{
		path: componentPagesMenu.content.subMenu.figures.path,
		element: <CONTENT.FIGURES />,
	},

	/**
	 * Forms
	 */
	{
		path: componentPagesMenu.forms.path,
		element: <FORMS_PAGE.FORMS />,
	},
	{
		path: componentPagesMenu.forms.subMenu.formGroup.path,
		element: <FORMS_PAGE.FORM_GROUP />,
	},
	{
		path: componentPagesMenu.forms.subMenu.formControl.path,
		element: <FORMS_PAGE.FORM_CONTROLS />,
	},
	{
		path: componentPagesMenu.forms.subMenu.select.path,
		element: <FORMS_PAGE.SELECT />,
	},
	{
		path: componentPagesMenu.forms.subMenu.checksAndRadio.path,
		element: <FORMS_PAGE.CHECKS_AND_RADIO />,
	},
	{
		path: componentPagesMenu.forms.subMenu.range.path,
		element: <FORMS_PAGE.RANGE />,
	},
	{
		path: componentPagesMenu.forms.subMenu.inputGroup.path,
		element: <FORMS_PAGE.INPUT_GROUP />,
	},
	{
		path: componentPagesMenu.forms.subMenu.validation.path,
		element: <FORMS_PAGE.VALIDATION />,
	},
	{
		path: componentPagesMenu.forms.subMenu.wizard.path,
		element: <FORMS_PAGE.WIZARD />,
	},

	/**
	 * Components
	 */
	{
		path: componentPagesMenu.components.path,
		element: <COMPONENTS_PAGE.COMPONENTS />,
	},
	{
		path: componentPagesMenu.components.subMenu.tooltip.path,
		element: <COMPONENTS_PAGE.TOOLTIP />,
	},
	{
		path: componentPagesMenu.components.subMenu.toasts.path,
		element: <COMPONENTS_PAGE.TOASTS />,
	},
	{
		path: componentPagesMenu.components.subMenu.scrollspy.path,
		element: <COMPONENTS_PAGE.SCROLLSPY />,
	},
	{
		path: componentPagesMenu.components.subMenu.carousel.path,
		element: <COMPONENTS_PAGE.CAROUSEL />,
	},
	{
		path: componentPagesMenu.components.subMenu.spinners.path,
		element: <COMPONENTS_PAGE.SPINNER />,
	},
	{
		path: componentPagesMenu.components.subMenu.listGroup.path,
		element: <COMPONENTS_PAGE.LIST_GROUP />,
	},
	{
		path: componentPagesMenu.components.subMenu.breadcrumb.path,
		element: <COMPONENTS_PAGE.BREADCRUMB />,
	},
	{
		path: componentPagesMenu.components.subMenu.collapse.path,
		element: <COMPONENTS_PAGE.COLLAPSE />,
	},
	{
		path: componentPagesMenu.components.subMenu.pagination.path,
		element: <COMPONENTS_PAGE.PAGINATION />,
	},
	{
		path: componentPagesMenu.components.subMenu.progress.path,
		element: <COMPONENTS_PAGE.PROGRESS />,
	},
	{
		path: componentPagesMenu.components.subMenu.card.path,
		element: <COMPONENTS_PAGE.CARD />,
	},
	{
		path: componentPagesMenu.components.subMenu.button.path,
		element: <COMPONENTS_PAGE.BUTTON />,
	},
	{
		path: componentPagesMenu.components.subMenu.buttonGroup.path,
		element: <COMPONENTS_PAGE.BUTTON_GROUP />,
	},
	{
		path: componentPagesMenu.components.subMenu.alert.path,
		element: <COMPONENTS_PAGE.ALERT />,
	},
	{
		path: componentPagesMenu.components.subMenu.badge.path,
		element: <COMPONENTS_PAGE.BADGE />,
	},
	{
		path: componentPagesMenu.components.subMenu.popovers.path,
		element: <COMPONENTS_PAGE.POPOVERS />,
	},
	{
		path: componentPagesMenu.components.subMenu.dropdowns.path,
		element: <COMPONENTS_PAGE.DROPDOWN />,
	},
	{
		path: componentPagesMenu.components.subMenu.accordion.path,
		element: <COMPONENTS_PAGE.ACCORDION />,
	},
	{
		path: componentPagesMenu.components.subMenu.modal.path,
		element: <COMPONENTS_PAGE.MODAL />,
	},
	{
		path: componentPagesMenu.components.subMenu.navsTabs.path,
		element: <COMPONENTS_PAGE.NAVS_TABS />,
	},
	{
		path: componentPagesMenu.components.subMenu.offcanvas.path,
		element: <COMPONENTS_PAGE.OFF_CANVAS />,
	},
	{
		path: componentPagesMenu.components.subMenu.table.path,
		element: <COMPONENTS_PAGE.TABLE />,
	},

	/**
	 * Utilities
	 */
	{
		path: componentPagesMenu.utilities.path,
		element: <UTILITIES.UTILITIES />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.api.path,
		element: <UTILITIES.API />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.background.path,
		element: <UTILITIES.BACKGROUND />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.borders.path,
		element: <UTILITIES.BORDERS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.colors.path,
		element: <UTILITIES.COLORS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.display.path,
		element: <UTILITIES.DISPLAY />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.flex.path,
		element: <UTILITIES.FLEX />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.float.path,
		element: <UTILITIES.FLOAT />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.interactions.path,
		element: <UTILITIES.INTERACTIONS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.overflow.path,
		element: <UTILITIES.OVERFLOW />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.position.path,
		element: <UTILITIES.POSITION />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.shadows.path,
		element: <UTILITIES.SHADOWS />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.sizing.path,
		element: <UTILITIES.SIZING />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.spacing.path,
		element: <UTILITIES.SPACING />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.text.path,
		element: <UTILITIES.TEXT />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.verticalAlign.path,
		element: <UTILITIES.VERTICAL_ALIGN />,
	},
	{
		path: componentPagesMenu.utilities.subMenu.visibility.path,
		element: <UTILITIES.VISIBILITY />,
	},

	/**
	 * Extra
	 */

	/**
	 * Icons
	 */
	{
		path: componentPagesMenu.icons.path,
		element: <ICONS.ICONS_LIST />,
	},
	{
		path: componentPagesMenu.icons.subMenu.icon.path,
		element: <ICONS.ICON />,
	},
	{
		path: componentPagesMenu.icons.subMenu.material.path,
		element: <ICONS.MATERIAL />,
	},

	/**
	 * Charts
	 */
	{
		path: componentPagesMenu.charts.path,
		element: <CHARTS_PAGE.CHART_LIST />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsUsage.path,
		element: <CHARTS_PAGE.GENERAL_USAGE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsSparkline.path,
		element: <CHARTS_PAGE.SPARKLINE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsLine.path,
		element: <CHARTS_PAGE.LINE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsArea.path,
		element: <CHARTS_PAGE.AREA />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsColumn.path,
		element: <CHARTS_PAGE.COLUMN />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsBar.path,
		element: <CHARTS_PAGE.BAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsMixed.path,
		element: <CHARTS_PAGE.MIXED />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsTimeline.path,
		element: <CHARTS_PAGE.TIMELINE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsCandleStick.path,
		element: <CHARTS_PAGE.CANDLESTICK />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsBoxWhisker.path,
		element: <CHARTS_PAGE.BOX_WHISKER />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsPieDonut.path,
		element: <CHARTS_PAGE.PIE_DONUT />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsRadar.path,
		element: <CHARTS_PAGE.RADAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsPolar.path,
		element: <CHARTS_PAGE.POLAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsRadialBar.path,
		element: <CHARTS_PAGE.RADIAL_BAR />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsBubble.path,
		element: <CHARTS_PAGE.BUBBLE />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsScatter.path,
		element: <CHARTS_PAGE.SCATTER />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsHeatMap.path,
		element: <CHARTS_PAGE.HEAT_MAP />,
	},
	{
		path: componentPagesMenu.charts.subMenu.chartsTreeMap.path,
		element: <CHARTS_PAGE.TREE_MAP />,
	},

	{
		path: componentPagesMenu.notification.path,
		element: <EXTRA.NOTIFICATION />,
	},
	{
		path: componentPagesMenu.hooks.path,
		element: <EXTRA.HOOKS />,
	},
];
const contents = [...presentation, ...documentation];

export default contents;
